import React from 'react';
import { Typography, Spin, Button } from 'antd';
import { Sparkles } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import {
  CarouselProvider,
  useCarouselContext,
  useVideoPreloadContext,
  UserInteractionsProvider,
  VideoPreloadProvider,
  ModalV2,
} from '@doer/frontend-components/carrusel';
import type { CarouselVideo } from '@doer/frontend-components/carrusel';
import { VideoGrid } from '../components/video-grid';
import { ExplainerCarousel } from '../components/explainer-carousel';
import { ShareModal } from '../components/share-modal';
import { SumarPostsModal, SumarPostsContent } from '../components/sumar-posts-modal';
import { VideoItem } from '../constants/videos';
import { tokens } from '../theme/tokens';
import { useSocialContext } from '../context/social';
import { isPublishable } from '../types/social';
import { HIDE_CATALOG } from '../config/demo';

const { Title, Text } = Typography;
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const toVideoItem = (v: CarouselVideo): VideoItem => ({
  id: v.id as string,
  title: v.title ?? '',
  b2_url: v.src,
  src_preview: v.src_preview,
  poster: v.poster,
  size_bytes: 0,
  uploaded_at: '',
  original_filename: '',
});

const HomeContent: React.FC = () => {
  const { videos, setCurrentItemIndex, setIsModalOpen, isModalOpen } = useCarouselContext();
  const { updatePreloadStatus } = useVideoPreloadContext();
  const location = useLocation();
  const loading = videos.length === 0;

  // Close the video modal on Escape. ModalV2 (frontend-components) ships no
  // keyboard handling of its own, but it reads open-state from the carousel
  // context — so we can drive it from here without touching that package:
  // listen for Escape while the modal is open and flip setIsModalOpen(false).
  React.useEffect(() => {
    if (!isModalOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsModalOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isModalOpen, setIsModalOpen]);

  // Accounts feed the "Sumar mis posts" modal (used in the entry-point card
  // below). Keeping this in HomeContent (not at the root) so it co-locates
  // with the entry button — the carousel context is unrelated.
  const { accounts: allAccounts } = useSocialContext();
  const publishableAccounts = React.useMemo(
    () => allAccounts.filter(a => isPublishable(a.provider)),
    [allAccounts]
  );
  const [sumarOpen, setSumarOpen] = React.useState(false);

  // Mark videos as preloaded as soon as we have data. The Modal v2 poster
  // overlay only hides when isPreloaded is true; without the Carousel-swipe
  // strip (which normally runs useVideoPreloader) it'd cover the playing
  // <video> and the user would hear audio with a frozen image.
  //
  // ⚠ `updatePreloadStatus` from VideoPreloadProvider is NOT memoized
  // upstream: every render of the provider creates a fresh function reference
  // AND every call creates a new state object (no value-equality bailout).
  // Putting it in the dep array creates an infinite render loop in React 19
  // ("Maximum update depth exceeded"). We mirror the latest version into a
  // ref and depend only on `videos`. Fixing this properly belongs upstream in
  // frontend-components (useCallback the setters), tracked separately.
  const updatePreloadStatusRef = React.useRef(updatePreloadStatus);
  React.useEffect(() => { updatePreloadStatusRef.current = updatePreloadStatus; });
  React.useEffect(() => {
    videos.forEach(v => updatePreloadStatusRef.current(v.id, true));
  }, [videos]);

  // Defensive cleanup: close the swipe modal and the body scroll lock the
  // moment the URL leaves home, before React unmounts this subtree. Without
  // this, on certain transitions the modal portal + body overflow:hidden could
  // linger long enough to make /ranking look like it still shows videos.
  React.useEffect(() => {
    if (!location.pathname.match(/^\/?$/)) {
      setIsModalOpen(false);
    }
  }, [location.pathname, setIsModalOpen]);

  // Final safety net: also force the modal closed on unmount, regardless of
  // route. If anything kept it open it gets reset here.
  React.useEffect(() => {
    return () => {
      setIsModalOpen(false);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenReel = (index: number) => {
    setCurrentItemIndex(index);
    setIsModalOpen(true);
  };

  // Stabilize props passed to SumarPostsModal — avoids creating a fresh array
  // every parent render, which used to invalidate the modal's internal
  // useMemo/useEffect chains.
  const modalAccounts = React.useMemo(
    () => publishableAccounts.map(a => ({ provider: a.provider, socialId: a.socialId, username: a.username })),
    [publishableAccounts]
  );

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 24px' }}>
      {/* ─── Carrusel Explicativo (hero "¿Como funciona?") ─────────────
          Mock de la diseñadora 2026-07-17: va ARRIBA del catálogo. */}
      <ExplainerCarousel />

      {/* ─── Header del catálogo — acknowledges BOTH paths to puntos ───
          Path A: share a video from the catalog below.
          Path B: register a post you already made on your own socials.
          The button is the secondary CTA (the primary, visually, is the
          grid itself); we still put it up here so the user doesn't have to
          scroll past the whole catalog to find it. Only shows the button
          if the user has at least one publishable account connected. */}
      <div
        style={{
          marginBottom: 32,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div style={{ flex: '1 1 380px', minWidth: 0 }}>
          <Title level={2} style={{ margin: '0 0 8px', color: tokens.colors.primary.neutral[900], fontWeight: 700 }}>
            Compartí estos videos o suma puntos con tu reels
          </Title>
          <Text style={{ color: tokens.colors.primary.neutral[500], fontSize: 15, lineHeight: 1.5, display: 'block' }}>
            Cada reproducción suma puntos para el ranking.
          </Text>
        </div>
        {publishableAccounts.length > 0 && (
          <Button
            type='primary'
            icon={<Sparkles size={15} />}
            onClick={() => setSumarOpen(true)}
            style={{
              background: tokens.colors.brand.gradient,
              border: 'none',
              fontWeight: 600,
              height: 40,
              paddingInline: 18,
              fontSize: 14,
              boxShadow: '0 4px 14px rgba(154, 33, 140, 0.25)',
            }}
          >
            Sumar puntos con mis reels
          </Button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Spin size='large' />
        </div>
      ) : (
        <VideoGrid videos={videos.map(toVideoItem)} onOpenReel={handleOpenReel} />
      )}

      <SumarPostsModal
        accounts={modalAccounts}
        open={sumarOpen}
        onClose={() => setSumarOpen(false)}
      />

      <ModalV2 />
      {/* The share button used to be a floating FAB rendered here via a
          portal. It now lives INSIDE ModalV2 (frontend-components), styled
          like the sound button (small dark circle, bottom-right). We pass
          onShareClick to CarouselProvider below — that's the trigger. */}
    </div>
  );
};

// ─── HIDE_CATALOG view ──────────────────────────────────────────────────────
// When the catalog is hidden, the home page IS the "Sumar mis posts" flow,
// embedded inline (the exact content of the modal, as if the button had been
// clicked). No catalog grid, no button, no carousel — so we don't mount
// CarouselProvider nor hit /carousel/social at all.
const SumarPostsOnlyPage: React.FC = () => {
  const { accounts: allAccounts } = useSocialContext();
  const publishableAccounts = React.useMemo(
    () => allAccounts.filter(a => isPublishable(a.provider)),
    [allAccounts]
  );
  const modalAccounts = React.useMemo(
    () => publishableAccounts.map(a => ({ provider: a.provider, socialId: a.socialId, username: a.username })),
    [publishableAccounts]
  );

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '40px 24px' }}>
      {/* Card chrome mirrors the dialog the content used to live in, so it
          still reads as a self-contained panel now that it's inline. */}
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 6px 28px rgba(0,0,0,0.06)',
          padding: '20px 8px 8px',
        }}
      >
        <SumarPostsContent accounts={modalAccounts} open onClose={() => {}} />
      </div>
    </div>
  );
};

export const HomePage: React.FC = () => {
  const [shareVideo, setShareVideo] = React.useState<VideoItem | null>(null);

  // CarouselProvider passes the CarouselVideo to the share callback; the
  // ShareModal expects our local VideoItem shape, so adapt at the boundary.
  const handleShare = React.useCallback((v: CarouselVideo) => {
    setShareVideo(toVideoItem(v));
  }, []);

  // Catalog hidden → render the embedded "Sumar mis posts" flow instead.
  if (HIDE_CATALOG) {
    return <SumarPostsOnlyPage />;
  }

  return (
    <UserInteractionsProvider>
      <VideoPreloadProvider>
        <CarouselProvider
          fetchUrl={`${API_BASE}/carousel/social`}
          widgetId='social-home'
          widgetSize='300x600'
          carouselVersion='v2'
          scrollDirection='vertical'
          theme='dark'
          onShareClick={handleShare}
        >
          <HomeContent />
        </CarouselProvider>
      </VideoPreloadProvider>

      {shareVideo && (
        <ShareModal video={shareVideo} onClose={() => setShareVideo(null)} />
      )}
    </UserInteractionsProvider>
  );
};
